import { z } from 'zod';

/**
 * Papéis RBAC. Hierarquia implícita:
 *   admin > publisher > revisor > editor
 *
 * - admin     : tudo, inclusive gestão de usuários e configurações
 * - publisher : publica artigos aprovados, agenda publicação
 * - revisor   : aprova/devolve artigos em revisão
 * - editor    : cria e edita rascunhos, submete para revisão
 *
 * `publisher` pode ser fundido em `admin` se o volume editorial for baixo.
 */
export const roleSchema = z.enum(['admin', 'publisher', 'revisor', 'editor']);
export type Role = z.infer<typeof roleSchema>;

/**
 * Nomes dos papéis como o usuário do painel os lê. O enum é técnico
 * (`publisher`, `revisor`) e nunca deve aparecer na interface.
 */
export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Administrador',
  publisher: 'Publicador',
  revisor: 'Revisor',
  editor: 'Editor',
};

/**
 * O que cada papel pode fazer, em uma linha, para exibir no painel.
 */
export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  admin: 'Acesso total, incluindo usuários e configurações do site.',
  publisher: 'Publica conteúdo no ar e edita todas as áreas do site.',
  revisor: 'Revisa e aprova textos, mas não publica.',
  editor: 'Escreve e edita rascunhos.',
};

export const ROLE_HIERARCHY: Record<Role, number> = {
  editor: 1,
  revisor: 2,
  publisher: 3,
  admin: 4,
};

export function roleHasAtLeast(have: Role, required: Role): boolean {
  return ROLE_HIERARCHY[have] >= ROLE_HIERARCHY[required];
}

export const loginInputSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(200),
});
export type LoginInput = z.infer<typeof loginInputSchema>;

export const userPublicSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  nome: z.string(),
  papel: roleSchema,
  ativo: z.boolean(),
  ultimoLogin: z.string().datetime({ offset: true }).nullable(),
  criadoEm: z.string().datetime({ offset: true }),
});
export type UserPublic = z.infer<typeof userPublicSchema>;

/** Regra de senha forte: ≥12 chars. Usada em create + reset. */
export const strongPasswordSchema = z
  .string()
  .min(12, 'Senha precisa ter pelo menos 12 caracteres.')
  .max(200);

export const createUserInputSchema = z.object({
  email: z.string().email('Verifique o formato do e-mail.').max(320),
  nome: z.string().min(2, 'Nome muito curto.').max(120),
  papel: roleSchema,
  senha: strongPasswordSchema,
});
export type CreateUserInput = z.infer<typeof createUserInputSchema>;

export const updateUserInputSchema = z.object({
  nome: z.string().min(2).max(120).optional(),
  papel: roleSchema.optional(),
  ativo: z.boolean().optional(),
});
export type UpdateUserInput = z.infer<typeof updateUserInputSchema>;

export const resetPasswordInputSchema = z.object({
  senha: strongPasswordSchema,
});
export type ResetPasswordInput = z.infer<typeof resetPasswordInputSchema>;

export const listUsersQuerySchema = z.object({
  q: z.string().max(120).optional(),
  papel: roleSchema.optional(),
  ativo: z.preprocess(
    (v) => (v === 'true' ? true : v === 'false' ? false : undefined),
    z.boolean().optional(),
  ),
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(100).default(20),
  sort: z.string().max(40).optional(),
});
export type ListUsersQuery = z.infer<typeof listUsersQuerySchema>;
